import React, { useEffect, useState } from 'react';
import {
  Box,
  Button,
  FormField,
  Input,
  Modal,
  SpaceBetween,
  Toggle,
} from '@amzn/awsui-components-core';
import { CustomFilterActionEnum } from './CustomFilterActionButtonDropdown';

type CustomFilterFormType =
  | CustomFilterActionEnum.SaveAs
  | CustomFilterActionEnum.Rename
  | CustomFilterActionEnum.Delete;

interface Props {
  defaultFilterName?: string;
  formType: CustomFilterFormType;
  onDismiss?: () => void;
  onSubmit?: (filterName?: string, isDefault?: boolean) => string;
  visible: boolean;
}

const CustomFilterFormModal: React.FunctionComponent<Props> = (props: Props) => {
  const { defaultFilterName, formType, onDismiss, onSubmit, visible } = props;
  const [filterName, setFilterName] = useState(defaultFilterName || '');
  const [isDefault, setIsDefault] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const modalHeader =
    formType === CustomFilterActionEnum.SaveAs
      ? 'Save filter view'
      : formType === CustomFilterActionEnum.Rename
        ? 'Rename filter view'
        : 'Delete filter view';
  const deleteConfirmationMsg = `Are you sure you want to delete: ${defaultFilterName} ?`;

  useEffect(() => {
    if (defaultFilterName) {
      setFilterName(defaultFilterName);
    }
  }, [defaultFilterName]);

  const resetForm = () => {
    setFilterName(defaultFilterName || '');
    setIsDefault(false);
    setErrorMessage('');
  };

  const onFilterNameChanged = (newFilterName: string) => {
    setFilterName(newFilterName);
    setErrorMessage('');
  };

  const onConfirm = () => {
    let validationError = '';
    if (onSubmit) {
      validationError = onSubmit(filterName, isDefault);
      setErrorMessage(validationError);
    }
    if (!validationError) {
      resetForm();
    }
  };

  const onCancel = () => {
    if (onDismiss) {
      onDismiss();
    }
    resetForm();
  };

  const renderDefaultToggle = () => {
    return formType === CustomFilterActionEnum.SaveAs ? (
      <Toggle onChange={({ detail }) => setIsDefault(detail.checked)} checked={isDefault}>
        {'Set as default'}
      </Toggle>
    ) : null;
  };

  const renderFormFields = () => {
    return formType === CustomFilterActionEnum.Delete ? (
      <div>{deleteConfirmationMsg}</div>
    ) : (
      <Box>
        <FormField
          label="Filter view name"
          errorText={errorMessage}
          i18nStrings={{ errorIconAriaLabel: 'Error' }}
        >
          <Input
            value={filterName}
            onChange={({ detail }) => onFilterNameChanged(detail.value)}
            ariaRequired
          />
        </FormField>
        {renderDefaultToggle()}
      </Box>
    );
  };

  const filterModal = (
    <Modal
      visible
      onDismiss={onCancel}
      footer={
        <Box float="right">
          <SpaceBetween direction="horizontal" size="xs">
            <Button variant="link" onClick={onCancel}>
              Cancel
            </Button>
            <Button
              variant="primary"
              disabled={formType !== CustomFilterActionEnum.Delete && !filterName}
              onClick={onConfirm}
            >
              Confirm
            </Button>
          </SpaceBetween>
        </Box>
      }
      header={modalHeader}
      closeAriaLabel="Close modal"
    >
      {renderFormFields()}
    </Modal>
  );

  if (!visible) {
    return null;
  }

  return filterModal;
};

export default CustomFilterFormModal;
